import { TrialDto } from "@decibeltrade/sdk";
import { useEffect, useState } from "react";

import { getServerTime } from "@/utils/server-time";

import { SETTLE_GRACE_MS, SETTLE_RESUBMIT_HOLD_MS } from "./constants";

/**
 * Whether the manual "Settle now" CTA should render. Manual settle backs up
 * the keeper, which normally finishes both settle legs within seconds of
 * expiry — the grace window keeps the button hidden while that happens (and
 * past on-chain expiry, avoiding ETRIAL_TOO_EARLY). After a submitted manual
 * settle the trial parks in Closing (async engine); the hold window gives the
 * keeper's flat-finalize a chance before re-offering the second leg.
 */
export function isSettleCtaVisible({
  expiresAtMs,
  nowMs,
  settleSubmittedAtMs,
}: {
  /** Undefined on degraded rows — treated as past grace so settle stays reachable. */
  expiresAtMs: number | undefined;
  nowMs: number;
  settleSubmittedAtMs: number | null;
}): boolean {
  if (expiresAtMs !== undefined && nowMs < expiresAtMs + SETTLE_GRACE_MS) {
    return false;
  }
  return settleSubmittedAtMs === null || nowMs >= settleSubmittedAtMs + SETTLE_RESUBMIT_HOLD_MS;
}

export type SettleCtaState = "hidden" | "cta" | "stalled";

/**
 * Past the gate, a submitted settle whose position is still live means the
 * close IOC stalled — retries abort (EFFT_HAS_RESIDUAL_POSITION) until the
 * sweeper drains it, so offer reassurance instead of a dead CTA.
 */
export function deriveSettleCtaState({
  gateOpen,
  closeAttempted,
  positionFlat,
}: {
  gateOpen: boolean;
  closeAttempted: boolean;
  positionFlat: boolean;
}): SettleCtaState {
  if (!gateOpen) return "hidden";
  return closeAttempted && !positionFlat ? "stalled" : "cta";
}

export function useSettleGate({
  trial,
  settleSubmittedAtMs,
}: {
  trial: TrialDto | null;
  settleSubmittedAtMs: number | null;
}): boolean {
  const isActive = trial?.status === "Active";
  const [nowMs, setNowMs] = useState(() => getServerTime());
  useEffect(() => {
    if (!isActive) return;
    const id = window.setInterval(() => setNowMs(getServerTime()), 1_000);
    return () => window.clearInterval(id);
  }, [isActive]);

  if (!isActive) return false;
  return isSettleCtaVisible({ expiresAtMs: trial.expires_at_ms, nowMs, settleSubmittedAtMs });
}
